import { useEffect, useState } from 'react'

import Action from 'ageas-react-component-action'
import Dropdown from 'ageas-react-component-dropdownlist'
import Link from 'ageas-react-component-link'
import MainLoader from 'ageas-react-component-loader'
import Section from 'ageas-react-component-section'
import TextField from 'ageas-react-component-textfield'

import { usePolicyholderDetailsStore } from '../../Stores/policyholderDetailsStore'
import { useGetAdditionalEntityDetailsStore } from '../../Stores/useGetAdditionalEntityDetailsStore'
import { useWarningStore } from '../../Stores/warningStore'
import { ADRUM_EVENT } from '../../constants/adrumEvent'
import { WARNING_BAR } from '../../constants/warningBar'
import { useContactUpdateEntityPayload } from '../../hooks/api/updateEntityDetails/useContactUpdateEntityPayload'
import { useUpdateEntityDetails } from '../../hooks/api/updateEntityDetails/useUpdateEntityDetails'
import { useAdrum } from '../../hooks/useAdrum'
import { t } from '../../i18n'
import { validateEmail, validatePhoneNumber } from '../../utils/validates'
import {
  ContactForm,
  FormContactValuesProps,
  MOBILE,
  initialContactFormValues,
} from './components/ContactForm'
import { ButtonSaveContainer } from './style'

export const Contacts = () => {
  const [showContact, setShowContact] = useState(false)
  const [enableAdrum, setEnableAdrum] = useState(true)
  const [isEmailPristine, setIsEmailPristine] = useState(false)
  const [formValues, setFormValues] = useState<FormContactValuesProps>()
  const { policyholderDetailsSelected, phoneSelected, setPhoneSelected } =
    usePolicyholderDetailsStore()
  const { setWarningOptions } = useWarningStore()
  const { policyholderAdditionalDetails } = useGetAdditionalEntityDetailsStore()
  const {
    mutate,
    data: dataUpdateEntity,
    isSuccess,
    isLoading,
  } = useUpdateEntityDetails()
  const { handleContactPayload, handleEditEmailPayload } =
    useContactUpdateEntityPayload()
  const [hasUpdate, setHasUpdate] = useState(false)
  const [emailValue, setEmailValue] = useState(
    policyholderAdditionalDetails?.emailContact?.email || '',
  )
  const [phoneListSelected, setPhoneListSelected] = useState<string>(
    phoneSelected ||
      policyholderAdditionalDetails?.phoneContactList?.[0]?.phoneNumber ||
      '',
  )

  const { startADRUMTagging } = useAdrum(!enableAdrum)

  const formattedPhoneList =
    policyholderAdditionalDetails?.phoneContactList?.map((phone) => ({
      caption: phone.phoneNumber,
      value: phone.phoneNumber,
    }))

  const validateContactForm = (): boolean => {
    const valuesValidation = {
      contactDescription: (formValues?.contactDescription?.length || 0) > 0,
      contactPhone: (formValues?.contactPhone?.length || 0) > 0,
      contactPhoneNumber: validatePhoneNumber(
        formValues?.contactPhoneNumber || '',
        formValues?.contactPhone === MOBILE,
      ),
    }

    return (
      valuesValidation.contactDescription &&
      valuesValidation.contactPhone &&
      valuesValidation.contactPhoneNumber
    )
  }

  const emailMessage = () => {
    const emailLength = emailValue.length

    if (emailLength > 0) {
      if (!validateEmail(emailValue)) {
        return t('pages.step3.contacts.invalidEmail')
      }
    } else if (emailLength === 0 && isEmailPristine) {
      return t('pages.step3.mandatory')
    }

    return ''
  }

  const handleEditEmail = () => {
    if (validateEmail(emailValue)) {
      mutate({
        payload: handleEditEmailPayload(emailValue),
        type: 'contact',
      })
    }
  }

  const handleUpdateEntitiesButton = () => {
    setHasUpdate(false)
    if (validateContactForm()) {
      mutate({
        payload: handleContactPayload(formValues as FormContactValuesProps),
        type: 'contact',
      })
    } else {
      setHasUpdate(true)
    }
  }

  useEffect(() => {
    if (isSuccess) {
      const phoneAdded =
        dataUpdateEntity.contactResponse?.phoneNumber?.toString()

      const currentPhone =
        policyholderAdditionalDetails?.phoneContactList?.find(
          (phone) => phone.phoneNumber === phoneAdded,
        )?.phoneNumber || ''

      setPhoneSelected(currentPhone)
      setPhoneListSelected(currentPhone)
      setShowContact(false)
      setIsEmailPristine(false)
      setWarningOptions({
        type: WARNING_BAR.type.success,
        labelTypes: [t('pages.step3.success.update')],
      })
    }
  }, [
    isSuccess,
    policyholderAdditionalDetails?.phoneContactList,
    setPhoneSelected,
    setWarningOptions,
  ])

  useEffect(() => {
    if (enableAdrum && isSuccess) {
      startADRUMTagging(ADRUM_EVENT.contact)
      setEnableAdrum(false)
    }
  }, [enableAdrum, isSuccess])

  return (
    <>
      <MainLoader show={isLoading} />
      <TextField
        id={`email-entityDetailsContactEmail-${isEmailPristine}`}
        name="entityDetailsContactEmail"
        key={`email-entityDetailsContactEmail-${isEmailPristine}`}
        label={t('entity.email')}
        message={emailMessage()}
        maxLength={70}
        value={emailValue}
        onValueChanged={(id: string, value: string) => {
          setIsEmailPristine(
            value !== policyholderAdditionalDetails?.emailContact?.email,
          )
          setEmailValue(value)
        }}
      />
      {isEmailPristine && (
        <ButtonSaveContainer>
          <div className="button-save">
            <Action
              id="contactAction"
              label={t('general.save')}
              type="button"
              disable={
                !validateEmail(emailValue) ||
                emailValue ===
                  policyholderAdditionalDetails?.emailContact?.email
              }
              onValueChanged={handleEditEmail}
            />
          </div>
        </ButtonSaveContainer>
      )}
      {(policyholderAdditionalDetails?.phoneContactList || [])?.length > 1 && (
        <Dropdown
          id={`contact-phone-owner-${phoneListSelected}`}
          key={`contact-phone-owner-${phoneListSelected}`}
          name="contact-phone-owner"
          label={t('pages.step3.contacts.phoneContact')}
          value={phoneListSelected}
          colorTheme="blue"
          version="2"
          options={formattedPhoneList}
          onValueChanged={(id: string, value: string) => {
            setPhoneListSelected(value)
            setPhoneSelected(value)
          }}
        />
      )}
      {(policyholderAdditionalDetails?.phoneContactList || [])?.length ===
        1 && (
        <TextField
          id="entityDetailsContactPhone"
          name="entityDetailsContactPhone"
          label={t('pages.step3.contacts.phoneContact')}
          readOnly
          value={
            policyholderDetailsSelected?.phoneContactsList?.[0]
              ?.contactNumber ||
            policyholderAdditionalDetails?.phoneContactList?.[0]?.phoneNumber ||
            ''
          }
        />
      )}
      {showContact && !isLoading && (
        <>
          <Section
            id="addressesTitle"
            name={t('pages.step3.contacts.newContact')}
            config={{ labelSize: '20px', labelBold: '500' }}
            colorTheme="blue"
          />
          <ContactForm
            hiddenTitle
            hiddenEmail
            initialFormValues={initialContactFormValues}
            onChange={(values) => setFormValues(values)}
            hasUpdate={hasUpdate}
          />
          <div className="add-content">
            <Link
              label={t('general.cancel')}
              onValueChanged={() => {
                setShowContact(false)
                setHasUpdate(false)
              }}
            />
          </div>
          <ButtonSaveContainer>
            <div className="button-save">
              <Action
                id="contactAction"
                label={t('general.save')}
                type="button"
                disable={!validateContactForm()}
                onValueChanged={() => handleUpdateEntitiesButton()}
              />
            </div>
          </ButtonSaveContainer>
        </>
      )}
      {!showContact && (
        <div className="add-content">
          <Link
            label={t('pages.step3.contacts.addContact')}
            onValueChanged={() => setShowContact(true)}
          />
        </div>
      )}
    </>
  )
}
